import React, { useEffect, useState, useRef } from 'react';
import InfoBanner from '../components/InfoBanner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import {
    fetchRelationalDatabases, deleteRelationalDatabase, addRelationalDatabase,
    updateRelationalDatabaseStatus, RelationalDatabase,
} from '../store/slices/relationalDatabasesSlice';
import { fetchFlavors } from '../store/slices/flavorsSlice';
import axiosInstance from '../utility/axiosInstance';
import { useToast } from '../components/ToastContext';
import { useConfirm } from '../components/ConfirmContext';
import Select, { SingleValue } from 'react-select';
import { getSelectStyles, getActionSelectStyles } from '../utility/selectStyles';
import Modal from '../components/Modal';
import ManagedBackupsModal from '../components/ManagedBackupsModal';
import TrialLock from '../components/TrialLock';
import useAutoOpenCreateModal from '../hooks/useAutoOpenCreateModal';
import { FaPlus, FaSyncAlt, FaRegCopy, FaCheck } from 'react-icons/fa';
import '../styles/InstancesPage.scss';
import '../styles/DatabasesPage.scss';
import { isFlavorOptionDisabled, platformImage, toFlavorOptions } from '../utility/flavorOptions';
import { NameField, SecretField, formatSizeOption } from '../components/CreateFormFields';
import { secretOk } from '../utility/fieldRules';

const ITEMS_PER_PAGE = 10;

type SelectOption = { value: string; label: string; isDisabled?: boolean };
type Engine = 'mysql' | 'mariadb';

const ENGINE_OPTIONS: { value: Engine; label: string }[] = [
    { value: 'mysql', label: 'MySQL' },
    { value: 'mariadb', label: 'MariaDB' },
];
const VERSIONS: Record<Engine, SelectOption[]> = {
    mysql: [{ value: '8.0', label: 'MySQL 8.0 (recommended)' }],
    mariadb: [{ value: '11.4', label: 'MariaDB 11.4 (recommended)' }, { value: '10.11', label: 'MariaDB 10.11 (LTS)' }],
};

const RelationalDbPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const [backupsFor, setBackupsFor] = useState<any>(null);
    const { databases, status } = useSelector((s: RootState) => s.relationalDatabases);
    const flavors = useSelector((s: RootState) => s.flavors.data);
    const images = useSelector((s: RootState) => s.images.data);
    const vmInstances = useSelector((s: RootState) => s.instances.data);

    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newDb, setNewDb] = useState<{
        name: string; engine: Engine; db_name: string; db_user: string; db_password: string;
        version: string;
    }>({ name: '', engine: 'mysql', db_name: 'app', db_user: 'appuser', db_password: '', version: '8.0' });
    const [flavorOption, setFlavorOption] = useState<SelectOption | null>(null);
    const [keypairOption, setKeypairOption] = useState<SelectOption | null>(null);
    const [keypairs, setKeypairs] = useState<SelectOption[]>([]);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [networkId, setNetworkId] = useState<string | null>(null);
    const [justCreated, setJustCreated] = useState<RelationalDatabase & { db_password: string } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const pollRefs = useRef<Record<number, NodeJS.Timeout>>({});

    const creationLocked = useAutoOpenCreateModal(setShowCreate);

    useEffect(() => {
        dispatch(fetchRelationalDatabases());
        dispatch(fetchFlavors());
    }, [dispatch]);

    useEffect(() => {
        if (!showCreate) return;
        Promise.all([
            axiosInstance.get('/v1/keypairs'),
            axiosInstance.get('/v1/private_networks'),
        ]).then(([kpRes, netRes]) => {
            const kps = (Array.isArray(kpRes.data) ? kpRes.data : [])
                .map((k: any) => ({ value: k.keypair?.name ?? '', label: k.keypair?.name ?? '' }))
                .filter((k: SelectOption) => k.value);
            setKeypairs(kps);
            if (kps.length === 1) setKeypairOption(kps[0]);
            const nets = Array.isArray(netRes.data) ? netRes.data : [];
            if (nets[0]?.id) setNetworkId(nets[0].id);
        }).catch((_e) => { /* ignore */ });
    }, [showCreate]);

    useEffect(() => {
        databases.filter(d => d.status === 'provisioning').forEach(db => {
            if (pollRefs.current[db.id]) return;
            pollRefs.current[db.id] = setInterval(async () => {
                try {
                    const listRes = await axiosInstance.get('/v1/instances');
                    const instances = Array.isArray(listRes.data) ? listRes.data : [];
                    const inst = instances.find((i: any) => i.name === `rdb-${db.name}` && i.status === 'ACTIVE');
                    if (inst) {
                        const ip = inst.metadata?.floatingIp ?? null;
                        clearInterval(pollRefs.current[db.id]);
                        delete pollRefs.current[db.id];
                        await axiosInstance.patch(`/v1/relational-databases/${db.id}`, { status: 'ready', floating_ip: ip });
                        dispatch(updateRelationalDatabaseStatus({ id: db.id, status: 'ready', floating_ip: ip }));
                    }
                } catch (_e) { /* keep polling */ }
            }, 15000);
        });
        return () => { Object.values(pollRefs.current).forEach(clearInterval); };
    }, [databases, dispatch]);

    useEffect(() => {
        setCurrentPage(0);
    }, [databases.length]);

    const totalDatabases = databases.length;
    const totalPages = Math.ceil(totalDatabases / ITEMS_PER_PAGE);
    const paginatedDatabases = databases.slice(
        currentPage * ITEMS_PER_PAGE,
        (currentPage + 1) * ITEMS_PER_PAGE
    );

    // Every number from /v1/flavors; a size the datastore image cannot boot is locked with the reason, from that image's
    // own min_ram/min_disk (/v1/images), as on the instance form. One machine's price.
    const flavorOptions: SelectOption[] = toFlavorOptions(flavors, undefined, { image: platformImage(images, 'datastore') });

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!flavorOption) { showToast('Select a server size', 'error'); return; }
        if (!keypairOption) { showToast('Select an SSH keypair', 'error'); return; }
        if (!networkId) { showToast('No private network found. Create one first.', 'error'); return; }
        if (!newDb.name.trim()) { showToast('Enter a name', 'error'); return; }

        setCreating(true);
        try {
            const res = await axiosInstance.post('/v1/relational-databases', {
                name: newDb.name.trim(),
                engine: newDb.engine,
                db_name: newDb.db_name.trim() || 'app',
                db_user: newDb.db_user.trim() || 'appuser',
                db_password: newDb.db_password.trim() || undefined,
                version: newDb.version,
                flavor_id: flavorOption.value,
                network_id: networkId,
                keypair_name: keypairOption.value,
                security_group_names: ['default'],
                imageName: 'ubuntu',
            });
            setJustCreated(res.data);
            dispatch(addRelationalDatabase(res.data));
            setShowCreate(false);
            setNewDb({ name: '', engine: 'mysql', db_name: 'app', db_user: 'appuser', db_password: '', version: '8.0' });
            setFlavorOption(null);
            showToast('Database provisioning started', 'success');
        } catch (err: any) {
            showToast(err.response?.data?.error ?? 'Failed to create database', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (db: RelationalDatabase) => {
        const proceed = await confirm({
            title: 'Delete Database',
            message: `Delete "${db.name}"? This will destroy the server and all data.`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger',
        });
        if (!proceed) return;
        setDeletingId(db.id);
        try {
            await dispatch(deleteRelationalDatabase(db.id)).unwrap();
            showToast(`${db.name} deleted`, 'success');
        } catch (err: any) {
            showToast(err.message ?? 'Failed to delete', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCopyConnection = (db: RelationalDatabase) => {
        const str = connectionString(db);
        if (!str) return;
        const doCopy = () => {
            if (navigator.clipboard && window.isSecureContext) {
                return navigator.clipboard.writeText(str);
            }
            const el = document.createElement('textarea');
            el.value = str;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.focus();
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return Promise.resolve();
        };
        doCopy().then(() => {
            setCopiedId(db.id);
            showToast('Copied - fill in the password shown when the database was created (it is not stored)', 'success');
            setTimeout(() => setCopiedId(null), 2000);
        }).catch(() => showToast('Failed to copy', 'error'));
    };

    // Same private-network access model as managed Postgres: reach it from an instance on the same
    // private network via the DB's internal IP. Falls back to the VM's fixed IP / floating IP.
    const hostFor = (db: RelationalDatabase): string | null => {
        if (db.private_ip) return db.private_ip;
        const vm: any = (vmInstances ?? []).find((i: any) => i.id === (db as any).instance_id);
        const internalIp = vm
            ? (Object.values(vm.addresses ?? {}).flat() as any[]).find(a => a?.["OS-EXT-IPS:type"] === "fixed")?.addr
            : null;
        return internalIp ?? db.floating_ip ?? null;
    };

    // MariaDB speaks the MySQL wire protocol, so a mysql:// URL works for both engines.
    const connectionString = (db: RelationalDatabase) => {
        const host = hostFor(db);
        return host ? `mysql://${db.db_user}:****@${host}:${db.port}/${db.db_name}` : null;
    };

    const fullConnectionString = (db: RelationalDatabase & { db_password?: string }) => {
        const host = hostFor(db);
        return host ? `mysql://${db.db_user}:${db.db_password ?? '****'}@${host}:${db.port}/${db.db_name}` : null;
    };

    const engineLabel = (db: RelationalDatabase) => `${db.engine === 'mariadb' ? 'MariaDB' : 'MySQL'} ${db.db_version}`;
    const isLoading = status === 'loading';

    return (
        <div className="keypairs-page">
            <div className="page-header">
                <p className="page-subtitle">
                    Dedicated MySQL / MariaDB servers. Ready to connect in minutes.
                </p>
                <a href="https://docs.redu.cloud/Guides/databases" target="_blank" rel="noopener noreferrer"
                   className="docs-link">
                    Documentation →
                </a>
            </div>

            <InfoBanner id="relational-db-private-access">
                <span>Managed databases are <strong>private</strong> - reachable only from an instance on the <strong>same private network</strong>, using the database&apos;s private IP (e.g. <code>mysql://appuser:****@10.1.x.x:3306/app</code>). They are not exposed to the public internet.</span>
            </InfoBanner>

            <TrialLock>
                <button className="create-button" onClick={() => setShowCreate(true)} title="New Database">
                    <FaPlus />
                    <span>New Database</span>
                </button>
            </TrialLock>

            {/* Just-created credentials banner - shown once */}
            {justCreated && (
                <div className="db-credentials-banner">
                    <div className="db-cred-header">
                        <span className="db-cred-title">✓ <strong>{justCreated.name}</strong> is provisioning</span>
                        <button className="db-cred-dismiss" onClick={() => setJustCreated(null)}>✕</button>
                    </div>
                    <p className="db-cred-note">Save these credentials now - the password won&apos;t be shown again.</p>
                    <div className="db-cred-grid">
                        <div className="db-cred-row"><span>Engine</span><code>{engineLabel(justCreated)}</code></div>
                        <div className="db-cred-row"><span>Host</span><code>{hostFor(justCreated) ?? 'Provisioning… check back in 5 min'}</code></div>
                        <div className="db-cred-row"><span>Port</span><code>3306</code></div>
                        <div className="db-cred-row"><span>Database</span><code>{justCreated.db_name}</code></div>
                        <div className="db-cred-row"><span>User</span><code>{justCreated.db_user}</code></div>
                        <div className="db-cred-row"><span>Password</span><code>{justCreated.db_password}</code></div>
                        {fullConnectionString(justCreated) && (
                            <div className="db-cred-row">
                                <span>Connection string</span>
                                <code className="db-cred-conn" onClick={() => { navigator.clipboard?.writeText(fullConnectionString(justCreated)!); showToast('Copied', 'success'); }}>
                                    {fullConnectionString(justCreated)} <span className="copy-hint">click to copy</span>
                                </code>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Desktop table */}
            <div className="desktop-view">
                <div className="table-container">
                    <table className="keypairs-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Engine</th>
                                <th>Database</th>
                                <th>Connection</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="loading-state">
                                        <FaSyncAlt className="spin" /> Loading databases...
                                    </td>
                                </tr>
                            ) : paginatedDatabases.length > 0 ? (
                                paginatedDatabases.map(db => {
                                    const conn = connectionString(db);
                                    return (
                                    <tr key={db.id}>
                                        <td className="keypair-name">{db.name}</td>
                                        <td>{engineLabel(db)}</td>
                                        <td><code>{db.db_name}</code></td>
                                        <td className="db-conn">
                                            {conn ? (
                                                <div className="db-conn-wrapper">
                                                    <code>{conn}</code>
                                                    <button
                                                        className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(db)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="db-provisioning">Provisioning…</span>
                                            )}
                                        </td>
                                        <td><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <Select
                                                options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Database' }]}
                                                onChange={opt => { if (opt?.value === 'delete') handleDelete(db); else if (opt?.value === 'backups') setBackupsFor(db); }}
                                                placeholder="Actions"
                                                isSearchable={false}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={getActionSelectStyles()}
                                            />
                                        </td>
                                    </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="empty-state">No databases yet</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px', borderTop: '1px solid var(--border)' }}>
                            <button
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >← Prev</button>
                            <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalDatabases)} of {totalDatabases}
                            </span>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={currentPage >= totalPages - 1}
                                style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                            >Next →</button>
                        </div>
                    )}
                </div>
            </div>

            {/* Mobile cards */}
            <div className="mobile-view">
                {isLoading ? (
                    <div className="loading-state">
                        <FaSyncAlt className="spin" />
                        <p>Loading databases...</p>
                    </div>
                ) : (
                    <div className="cards-container">
                        {paginatedDatabases.length > 0 ? paginatedDatabases.map(db => {
                            const conn = connectionString(db);
                            return (
                            <div key={db.id} className="keypair-card">
                                <div className="card-header">
                                    <h3 className="card-title">{db.name}</h3>
                                    <div style={{ width: '100%', marginTop: 12 }}>
                                        <Select
                                            options={[{ value: 'backups', label: 'Backups' }, { value: 'delete', label: 'Delete Database' }]}
                                            onChange={opt => { if (opt?.value === 'delete') handleDelete(db); else if (opt?.value === 'backups') setBackupsFor(db); }}
                                            placeholder="Actions"
                                            isSearchable={false}
                                            menuPortalTarget={document.body}
                                            styles={getActionSelectStyles()}
                                        />
                                    </div>
                                </div>
                                <div className="card-content">
                                    <div className="fingerprint"><span className="label">Engine:</span><span className="value">{engineLabel(db)}</span></div>
                                    <div className="fingerprint"><span className="label">Database:</span><span className="value">{db.db_name}</span></div>
                                    <div className="fingerprint"><span className="label">Status:</span><span className="value"><span className={`db-status db-status-${deletingId === db.id ? 'provisioning' : db.status}`}>{deletingId === db.id ? 'deleting…' : db.status}</span></span></div>
                                    <div className="fingerprint">
                                        <span className="label">Connection:</span>
                                        <span className="value db-conn">
                                            {conn ? (
                                                <span className="db-conn-wrapper">
                                                    <span>{conn}</span>
                                                    <button
                                                        className={`db-copy-btn${copiedId === db.id ? ' copied' : ''}`}
                                                        onClick={() => handleCopyConnection(db)}
                                                        title="Copy connection string"
                                                    >
                                                        {copiedId === db.id ? <FaCheck /> : <FaRegCopy />}
                                                    </button>
                                                </span>
                                            ) : 'Provisioning…'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            );
                        }) : (
                            <div className="empty-state"><p>No databases yet</p></div>
                        )}
                        {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                    disabled={currentPage === 0}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >← Prev</button>
                                <span style={{ lineHeight: '30px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                                    {currentPage * ITEMS_PER_PAGE + 1}-{Math.min((currentPage + 1) * ITEMS_PER_PAGE, totalDatabases)} of {totalDatabases}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                                    disabled={currentPage >= totalPages - 1}
                                    style={{ padding: '5px 14px', background: 'none', border: '1px solid var(--border)', borderRadius: '6px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, color: 'var(--text-primary)' }}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Create modal */}
            <ManagedBackupsModal
                isOpen={!!backupsFor}
                onClose={() => setBackupsFor(null)}
                apiBase="/v1/relational-databases"
                serviceId={backupsFor?.id ?? null}
                serviceName={backupsFor?.name ?? ''}
            />

            <Modal isOpen={showCreate && !creationLocked} onClose={() => setShowCreate(false)} title="New MySQL / MariaDB Database">
                <form onSubmit={handleCreate} className="create-form">
                    <NameField value={newDb.name} onChange={v => setNewDb(p => ({ ...p, name: v }))} placeholder="my-database" />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group">
                            <label>Engine</label>
                            <Select
                                options={ENGINE_OPTIONS}
                                value={ENGINE_OPTIONS.find(o => o.value === newDb.engine)}
                                onChange={(v: SingleValue<{ value: Engine; label: string }>) => {
                                    const engine = (v?.value ?? 'mysql') as Engine;
                                    setNewDb(p => ({ ...p, engine, version: VERSIONS[engine][0].value }));
                                }}
                                styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                            />
                        </div>
                        <div className="form-group">
                            <label>Version</label>
                            <Select
                                options={VERSIONS[newDb.engine]}
                                value={VERSIONS[newDb.engine].find(o => o.value === newDb.version) ?? VERSIONS[newDb.engine][0]}
                                onChange={(v: SingleValue<SelectOption>) => setNewDb(p => ({ ...p, version: v?.value ?? VERSIONS[p.engine][0].value }))}
                                styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                            />
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="form-group">
                            <label>Database name</label>
                            <input value={newDb.db_name} onChange={e => setNewDb(p => ({ ...p, db_name: e.target.value }))} placeholder="app" />
                        </div>
                        <div className="form-group">
                            <label>Username</label>
                            <input value={newDb.db_user} onChange={e => setNewDb(p => ({ ...p, db_user: e.target.value }))} placeholder="appuser" />
                        </div>
                    </div>
                    <SecretField label="Password" value={newDb.db_password} onChange={v => setNewDb(p => ({ ...p, db_password: v }))} charset />
                    <div className="form-group">
                        <label>Server size <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={flavorOptions} value={flavorOption}
                            isOptionDisabled={isFlavorOptionDisabled as any}
                            onChange={(v: SingleValue<SelectOption>) => setFlavorOption(v)}
                            formatOptionLabel={formatSizeOption}
                            placeholder="Select server size…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="form-group">
                        <label>SSH keypair <span style={{ color: 'var(--color-error,#e53e3e)' }}>*</span></label>
                        <Select
                            options={keypairs} value={keypairOption}
                            onChange={(v: SingleValue<SelectOption>) => setKeypairOption(v)}
                            placeholder="Select keypair…" styles={getSelectStyles()} menuPortalTarget={document.body} menuPosition="fixed"
                        />
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCreate(false)}>Cancel</button>
                        <button type="submit" className="submit-button" disabled={creating || !flavorOption || !keypairOption || !newDb.name || !secretOk(newDb.db_password, { charset: true })}>
                            {creating ? 'Creating…' : 'Create Database'}
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default RelationalDbPage;
